import { ReactNode, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Captions, Image as ImageIcon, Trash2, Type, Video } from "lucide-react";
import { Clock, Film, QrCode, Star, UserRound, Youtube } from "lucide-react";
import { ArrowDownToLine, ArrowUpToLine, ChevronDown, ChevronRight, Copy } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { CaptionMode, FitMode, ReviewSource, Rotation, Slide } from "@shared/types";
import { FIT_MODES, ROTATIONS } from "@shared/playlist";
import { ApiError, mediaUrl, MediaUploadResult } from "@/lib/api";
import { parseYouTubeId } from "@/lib/youtube";
import { parseVimeo } from "@/lib/vimeo";
import { slideLabel } from "@/lib/slide-list-ops";
import { cn } from "@/lib/utils";
import { ScheduleEditor } from "@/components/admin/ScheduleEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Row-level controls shared by slide and group-reference cards. */
export interface RowControlProps {
  index: number;
  total: number;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
  onMoveTo: (edge: "top" | "bottom") => void;
  onDuplicate: () => void;
}

interface SlideCardProps extends RowControlProps {
  slide: Exclude<Slide, { type: "groupref" }>;
  uploadMedia: (file: File) => Promise<MediaUploadResult>;
  onChange: (slide: Slide) => void;
  onExpand?: () => void;
}

const TYPE_ICON = {
  image: ImageIcon,
  video: Video,
  text: Type,
  clock: Clock,
  youtube: Youtube,
  vimeo: Film,
  review: Star,
  qr: QrCode,
  team: UserRound,
} as const;

/** Split a filename into [base, extension] keeping the dot, e.g. "a.png" -> ["a", ".png"]. */
function splitExt(name: string): [string, string] {
  const i = name.lastIndexOf(".");
  if (i > 0) return [name.slice(0, i), name.slice(i)];
  // ".png" (base cleared during a rename): keep it in the extension slot so
  // the extension can never migrate into the editable base and get dropped.
  if (i === 0) return ["", name];
  return [name, ""];
}

export function SlideCard({ slide, uploadMedia, onChange, onExpand, ...row }: SlideCardProps) {
  const Icon = TYPE_ICON[slide.type];
  const vttInput = useRef<HTMLInputElement>(null);
  const [uploadingVtt, setUploadingVtt] = useState(false);
  const photoInput = useRef<HTMLInputElement>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  function patch<S extends Slide>(changes: Partial<S>) {
    onChange({ ...slide, ...changes } as Slide);
  }

  async function onPhotoSelected(file: File | undefined) {
    if (!file || slide.type !== "team") return;
    setUploadingPhoto(true);
    setUploadError(null);
    try {
      const res = await uploadMedia(file);
      patch<typeof slide>({ photoKey: res.key });
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : "Photo upload failed");
    } finally {
      setUploadingPhoto(false);
    }
  }

  async function onVttSelected(file: File | undefined) {
    if (!file || slide.type !== "video") return;
    setUploadingVtt(true);
    setUploadError(null);
    try {
      const res = await uploadMedia(file);
      patch<typeof slide>({ captions: { vttKey: res.key } });
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : "Caption upload failed");
    } finally {
      setUploadingVtt(false);
    }
  }

  const captionValue: string =
    slide.type === "video"
      ? typeof slide.captions === "object"
        ? "custom"
        : slide.captions
      : "off";

  return (
    <div className={cn("rounded-lg border bg-card", row.collapsed ? "p-2" : "p-4")}>
      <RowHeader
        {...row}
        id={slide.id}
        enabled={slide.enabled}
        onEnabledChange={(v) => patch({ enabled: v })}
        removeLabel="Remove slide"
        thumbnail={
          <button
            type="button"
            onClick={onExpand}
            title="Click to expand"
            className="block w-full cursor-zoom-in"
          >
            <SlidePreview slide={slide} />
          </button>
        }
        title={
          <>
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-label={slide.type} />
            <span className="truncate">{slideLabel(slide) || slide.type}</span>
          </>
        }
        badges={slide.schedule && <Badge variant="secondary">Scheduled</Badge>}
      />

      {uploadError && <p className="mt-3 text-sm text-destructive">{uploadError}</p>}

      {!row.collapsed && (
        <div className="mt-3 grid gap-4 md:grid-cols-[200px_1fr]">
          <button
            type="button"
            onClick={onExpand}
            title="Click to expand"
            className="block w-full cursor-zoom-in"
          >
            <SlidePreview slide={slide} />
          </button>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Group (optional)">
              <Input
                value={slide.group ?? ""}
                onChange={(e) => patch({ group: e.target.value })}
                placeholder="e.g. Intro"
              />
            </Field>

            {slide.type !== "video" && (
              <Field label="Duration (seconds)">
                <Input
                  type="number"
                  min={1}
                  max={3600}
                  value={slide.durationSec}
                  onChange={(e) => patch({ durationSec: Number(e.target.value) })}
                />
              </Field>
            )}

            {(slide.type === "image" || slide.type === "video") && (
              <>
                <Field label="File name" className="sm:col-span-2">
                  <div className="flex items-center gap-1">
                    <Input
                      value={splitExt(slide.fileName ?? "")[0]}
                      onChange={(e) => {
                        const ext = splitExt(slide.fileName ?? "")[1];
                        patch({ fileName: e.target.value + ext });
                      }}
                      placeholder="file name"
                    />
                    <span className="text-sm text-muted-foreground">
                      {splitExt(slide.fileName ?? "")[1]}
                    </span>
                  </div>
                </Field>
                <Field label="Fit">
                  <Select value={slide.fit} onValueChange={(v) => patch({ fit: v as FitMode })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIT_MODES.map((f) => (
                        <SelectItem key={f} value={f} className="capitalize">
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Rotation">
                  <Select
                    value={String(slide.rotation)}
                    onValueChange={(v) => patch({ rotation: Number(v) as Rotation })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROTATIONS.map((r) => (
                        <SelectItem key={r} value={String(r)}>
                          {r}°
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                {slide.type === "image" && (
                  <ToggleRow
                    label="Ken Burns (slow pan & zoom)"
                    checked={slide.kenBurns ?? false}
                    onChange={(v) => patch({ kenBurns: v })}
                  />
                )}
              </>
            )}

            {slide.type === "video" && (
              <>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <Label className="text-sm">Audio</Label>
                  <Switch checked={slide.audio} onCheckedChange={(v) => patch({ audio: v })} />
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <Label className="text-sm">Loop</Label>
                  <Switch checked={slide.loop} onCheckedChange={(v) => patch({ loop: v })} />
                </div>
                <Field label="Captions">
                  <Select
                    value={captionValue}
                    onValueChange={(v) => {
                      const next: CaptionMode =
                        v === "custom"
                          ? typeof slide.captions === "object"
                            ? slide.captions
                            : { vttKey: "" }
                          : (v as "off" | "auto");
                      patch({ captions: next });
                      if (v === "custom" && typeof slide.captions !== "object") vttInput.current?.click();
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">Off</SelectItem>
                      <SelectItem value="auto">Auto (embedded)</SelectItem>
                      <SelectItem value="custom">Custom .vtt</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    “Auto” only works if the file already has captions. For guaranteed subtitles,
                    upload a .vtt — or use a YouTube slide (auto-captioned).
                  </p>
                </Field>
                {captionValue === "custom" && (
                  <div className="flex items-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploadingVtt}
                      onClick={() => vttInput.current?.click()}
                    >
                      <Captions className="mr-1 h-4 w-4" />
                      {uploadingVtt ? "Uploading…" : "Upload .vtt"}
                    </Button>
                    {typeof slide.captions === "object" && slide.captions.vttKey && (
                      <span className="truncate text-xs text-muted-foreground">attached</span>
                    )}
                    <input
                      ref={vttInput}
                      type="file"
                      accept=".vtt,text/vtt"
                      className="hidden"
                      onChange={(e) => void onVttSelected(e.target.files?.[0])}
                    />
                  </div>
                )}
              </>
            )}

            {slide.type === "text" && (
              <>
                <Field label="Title" className="sm:col-span-2">
                  <Input value={slide.title} onChange={(e) => patch({ title: e.target.value })} />
                </Field>
                <Field label="Body" className="sm:col-span-2">
                  <Textarea value={slide.body} onChange={(e) => patch({ body: e.target.value })} />
                </Field>
                <Field label="Background">
                  <Input
                    type="color"
                    className="h-9 w-full p-1"
                    value={slide.bgColor}
                    onChange={(e) => patch({ bgColor: e.target.value })}
                  />
                </Field>
                <Field label="Text color">
                  <Input
                    type="color"
                    className="h-9 w-full p-1"
                    value={slide.textColor}
                    onChange={(e) => patch({ textColor: e.target.value })}
                  />
                </Field>
                <Field label="Alignment">
                  <Select
                    value={slide.align}
                    onValueChange={(v) => patch({ align: v as "left" | "center" | "right" })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </>
            )}

            {slide.type === "clock" && (
              <>
                <ToggleRow label="Show date" checked={slide.showDate} onChange={(v) => patch({ showDate: v })} />
                <ToggleRow
                  label="Show seconds"
                  checked={slide.showSeconds}
                  onChange={(v) => patch({ showSeconds: v })}
                />
                <ToggleRow label="24-hour time" checked={slide.hour24} onChange={(v) => patch({ hour24: v })} />
                <ToggleRow
                  label="Show weather"
                  checked={slide.showWeather}
                  onChange={(v) => patch({ showWeather: v })}
                />
                {slide.showWeather && (
                  <>
                    <Field label="Weather location (city)">
                      <Input
                        value={slide.locationName ?? ""}
                        onChange={(e) => patch({ locationName: e.target.value })}
                        placeholder="Honolulu"
                      />
                    </Field>
                    <Field label="Temperature unit">
                      <Select value={slide.tempUnit} onValueChange={(v) => patch({ tempUnit: v as "F" | "C" })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="F">Fahrenheit (°F)</SelectItem>
                          <SelectItem value="C">Celsius (°C)</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  </>
                )}
                <Field label="Background">
                  <Input
                    type="color"
                    className="h-9 w-full p-1"
                    value={slide.bgColor}
                    onChange={(e) => patch({ bgColor: e.target.value })}
                  />
                </Field>
                <Field label="Text color">
                  <Input
                    type="color"
                    className="h-9 w-full p-1"
                    value={slide.textColor}
                    onChange={(e) => patch({ textColor: e.target.value })}
                  />
                </Field>
              </>
            )}

            {slide.type === "youtube" && (
              <>
                <Field label="YouTube URL or video ID" className="sm:col-span-2">
                  <Input
                    defaultValue={slide.videoId}
                    onChange={(e) => {
                      const id = parseYouTubeId(e.target.value);
                      if (id) patch({ videoId: id });
                    }}
                    placeholder="https://www.youtube.com/watch?v=…"
                  />
                  {slide.videoId ? (
                    <p className="mt-1 text-xs text-muted-foreground">Video id: {slide.videoId}</p>
                  ) : (
                    <p className="mt-1 text-xs text-destructive">Paste a valid YouTube link to set the video.</p>
                  )}
                </Field>
                <Field label="Label (optional)">
                  <Input value={slide.title ?? ""} onChange={(e) => patch({ title: e.target.value })} />
                </Field>
                <ToggleRow label="Loop" checked={slide.loop} onChange={(v) => patch({ loop: v })} />
              </>
            )}

            {slide.type === "vimeo" && (
              <>
                <Field label="Vimeo URL or video ID" className="sm:col-span-2">
                  <Input
                    defaultValue={slide.videoId}
                    onChange={(e) => {
                      const ref = parseVimeo(e.target.value);
                      if (ref) patch({ videoId: ref.id, hash: ref.hash });
                    }}
                    placeholder="https://vimeo.com/123456789"
                  />
                  {slide.videoId ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Video id: {slide.videoId}
                      {slide.hash ? ` · hash: ${slide.hash}` : ""}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-destructive">Paste a valid Vimeo link to set the video.</p>
                  )}
                </Field>
                <Field label="Label (optional)">
                  <Input value={slide.title ?? ""} onChange={(e) => patch({ title: e.target.value })} />
                </Field>
                <ToggleRow label="Loop" checked={slide.loop} onChange={(v) => patch({ loop: v })} />
              </>
            )}

            {slide.type === "team" && (
              <>
                <Field label="Photo" className="sm:col-span-2">
                  <div className="flex items-center gap-4">
                    <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border bg-muted/40">
                      {slide.photoKey ? (
                        <img src={mediaUrl(slide.photoKey)} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <UserRound className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploadingPhoto}
                      onClick={() => photoInput.current?.click()}
                    >
                      {uploadingPhoto ? "Uploading…" : slide.photoKey ? "Replace photo" : "Upload photo"}
                    </Button>
                    {slide.photoKey && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => patch({ photoKey: undefined })}
                      >
                        Remove
                      </Button>
                    )}
                    <input
                      ref={photoInput}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => void onPhotoSelected(e.target.files?.[0])}
                    />
                  </div>
                </Field>
                <Field label="Name">
                  <Input value={slide.name} onChange={(e) => patch({ name: e.target.value })} />
                </Field>
                <Field label="Role / title">
                  <Input
                    value={slide.role}
                    onChange={(e) => patch({ role: e.target.value })}
                    placeholder="Family Physician"
                  />
                </Field>
                <Field label="Short bio" className="sm:col-span-2">
                  <Textarea value={slide.bio} onChange={(e) => patch({ bio: e.target.value })} />
                </Field>
                <Field label="Background">
                  <Input
                    type="color"
                    className="h-9 w-full p-1"
                    value={slide.bgColor}
                    onChange={(e) => patch({ bgColor: e.target.value })}
                  />
                </Field>
                <Field label="Text color">
                  <Input
                    type="color"
                    className="h-9 w-full p-1"
                    value={slide.textColor}
                    onChange={(e) => patch({ textColor: e.target.value })}
                  />
                </Field>
              </>
            )}

            {slide.type === "qr" && (
              <>
                <Field label="Link or text to encode" className="sm:col-span-2">
                  <Input
                    value={slide.data}
                    onChange={(e) => patch({ data: e.target.value })}
                    placeholder="https://g.page/r/your-review-link"
                  />
                  {!slide.data && (
                    <p className="mt-1 text-xs text-destructive">Add a link so the code works.</p>
                  )}
                </Field>
                <Field label="Heading">
                  <Input value={slide.title ?? ""} onChange={(e) => patch({ title: e.target.value })} />
                </Field>
                <Field label="Caption">
                  <Input
                    value={slide.caption ?? ""}
                    onChange={(e) => patch({ caption: e.target.value })}
                  />
                </Field>
                <Field label="Background">
                  <Input
                    type="color"
                    className="h-9 w-full p-1"
                    value={slide.bgColor}
                    onChange={(e) => patch({ bgColor: e.target.value })}
                  />
                </Field>
                <Field label="Code color">
                  <Input
                    type="color"
                    className="h-9 w-full p-1"
                    value={slide.fgColor}
                    onChange={(e) => patch({ fgColor: e.target.value })}
                  />
                </Field>
              </>
            )}

            {slide.type === "review" && (
              <>
                <Field label="Review text" className="sm:col-span-2">
                  <Textarea
                    value={slide.quote}
                    onChange={(e) => patch({ quote: e.target.value })}
                    placeholder="Best clinic in town — the staff were so kind…"
                  />
                </Field>
                <Field label="Reviewer name">
                  <Input
                    value={slide.author}
                    onChange={(e) => patch({ author: e.target.value })}
                    placeholder="Jane D."
                  />
                </Field>
                <Field label="Rating">
                  <Select
                    value={String(slide.rating)}
                    onValueChange={(v) => patch({ rating: Number(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 4, 3, 2, 1].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {"★".repeat(n)} ({n})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Source">
                  <Select
                    value={slide.source}
                    onValueChange={(v) => patch({ source: v as ReviewSource })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="google">Google</SelectItem>
                      <SelectItem value="yelp">Yelp</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Background">
                  <Input
                    type="color"
                    className="h-9 w-full p-1"
                    value={slide.bgColor}
                    onChange={(e) => patch({ bgColor: e.target.value })}
                  />
                </Field>
                <Field label="Text color">
                  <Input
                    type="color"
                    className="h-9 w-full p-1"
                    value={slide.textColor}
                    onChange={(e) => patch({ textColor: e.target.value })}
                  />
                </Field>
              </>
            )}

            <ToggleRow
              label="Hide clinic logo on this slide"
              checked={slide.hideLogo ?? false}
              onChange={(v) => patch({ hideLogo: v })}
            />

            <ScheduleEditor schedule={slide.schedule} onChange={(sch) => patch({ schedule: sch })} />
          </div>
        </div>
      )}
    </div>
  );
}

/** Checkbox, collapse chevron, label and the row's action buttons. When
 *  collapsed the thumbnail sits in the row itself. */
export function RowHeader({
  id,
  index,
  total,
  collapsed,
  onToggleCollapsed,
  selected,
  onSelectedChange,
  enabled,
  onEnabledChange,
  onRemove,
  onMove,
  onMoveTo,
  onDuplicate,
  removeLabel,
  thumbnail,
  title,
  badges,
  extra,
}: RowControlProps & {
  id: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  removeLabel: string;
  thumbnail: ReactNode;
  title: ReactNode;
  badges?: ReactNode;
  extra?: ReactNode;
}) {
  const Chevron = collapsed ? ChevronRight : ChevronDown;
  const first = index === 0;
  const last = index === total - 1;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <input
          type="checkbox"
          className="h-4 w-4 shrink-0 accent-primary"
          checked={selected}
          onChange={(e) => onSelectedChange(e.target.checked)}
          aria-label={`Select slide ${index + 1}`}
        />
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 shrink-0"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Expand slide" : "Collapse slide"}
        >
          <Chevron className="h-4 w-4" />
        </Button>
        {collapsed && <div className="w-20 shrink-0">{thumbnail}</div>}
        <Badge variant="outline" className="shrink-0 font-mono">
          {index + 1}
        </Badge>
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="flex min-w-0 items-center gap-2 text-left text-sm font-medium"
        >
          {title}
        </button>
        {badges}
      </div>
      <div className="flex flex-wrap items-center gap-0.5">
        <div className="mr-2 flex items-center gap-2">
          <Label htmlFor={`enabled-${id}`} className="text-xs text-muted-foreground">
            Enabled
          </Label>
          <Switch id={`enabled-${id}`} checked={enabled} onCheckedChange={onEnabledChange} />
        </div>
        {extra}
        <RowButton label="Move to top" disabled={first} onClick={() => onMoveTo("top")}>
          <ArrowUpToLine />
        </RowButton>
        <RowButton label="Move up" disabled={first} onClick={() => onMove(-1)}>
          <ArrowUp />
        </RowButton>
        <RowButton label="Move down" disabled={last} onClick={() => onMove(1)}>
          <ArrowDown />
        </RowButton>
        <RowButton label="Move to bottom" disabled={last} onClick={() => onMoveTo("bottom")}>
          <ArrowDownToLine />
        </RowButton>
        <RowButton label="Duplicate" onClick={onDuplicate}>
          <Copy />
        </RowButton>
        <RowButton label={removeLabel} onClick={onRemove}>
          <Trash2 />
        </RowButton>
      </div>
    </div>
  );
}

function RowButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      size="icon"
      variant="ghost"
      className="h-8 w-8"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      {children}
    </Button>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border p-3">
      <Label className="text-sm">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <Label className="mb-1.5 block text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function SlidePreview({ slide }: { slide: Slide }) {
  return (
    <div className="aspect-video overflow-hidden rounded-md border bg-black">
      {slide.type === "image" && (
        <img src={mediaUrl(slide.mediaKey)} alt="" className="h-full w-full object-contain" />
      )}
      {slide.type === "video" && (
        <video src={mediaUrl(slide.mediaKey)} className="h-full w-full object-contain" muted />
      )}
      {slide.type === "text" && (
        <div
          className="flex h-full w-full items-center justify-center p-2 text-center text-xs"
          style={{ backgroundColor: slide.bgColor, color: slide.textColor }}
        >
          {slide.title || "Text slide"}
        </div>
      )}
      {slide.type === "review" && (
        <div
          className="flex h-full w-full flex-col items-center justify-center p-2 text-center text-xs"
          style={{ backgroundColor: slide.bgColor, color: slide.textColor }}
        >
          <div style={{ color: "#fbbf24" }}>{"★".repeat(Math.min(5, Math.max(1, slide.rating)))}</div>
          <div className="line-clamp-2 opacity-90">{slide.quote || "Review"}</div>
        </div>
      )}
      {slide.type === "clock" && (
        <div
          className="flex h-full w-full flex-col items-center justify-center text-center"
          style={{ backgroundColor: slide.bgColor, color: slide.textColor }}
        >
          <div className="text-lg font-bold tabular-nums">12:00</div>
          {slide.showWeather && <div className="text-[10px] opacity-80">☀️ 72°{slide.tempUnit}</div>}
        </div>
      )}
      {slide.type === "youtube" &&
        (slide.videoId ? (
          <img
            src={`https://i.ytimg.com/vi/${slide.videoId}/hqdefault.jpg`}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-white/50">
            YouTube
          </div>
        ))}
      {slide.type === "vimeo" && (
        <div className="flex h-full w-full items-center justify-center gap-1 text-xs text-white/60">
          <Film className="h-4 w-4" /> Vimeo
        </div>
      )}
      {slide.type === "team" && (
        <div
          className="flex h-full w-full flex-col items-center justify-center gap-1 p-2 text-center text-xs"
          style={{ backgroundColor: slide.bgColor, color: slide.textColor }}
        >
          {slide.photoKey ? (
            <img src={mediaUrl(slide.photoKey)} alt="" className="h-10 w-10 rounded-full object-cover" />
          ) : (
            <UserRound className="h-8 w-8 opacity-60" />
          )}
          <span className="line-clamp-1">{slide.name || "Team member"}</span>
        </div>
      )}
      {slide.type === "qr" && (
        <div
          className="flex h-full w-full items-center justify-center"
          style={{ backgroundColor: slide.bgColor }}
        >
          {slide.data ? (
            <QRCodeSVG value={slide.data} size={64} bgColor={slide.bgColor} fgColor={slide.fgColor} />
          ) : (
            <span className="flex items-center gap-1 text-xs" style={{ color: slide.fgColor }}>
              <QrCode className="h-4 w-4" /> QR
            </span>
          )}
        </div>
      )}
    </div>
  );
}
